/**
 * ECO Settings Management
 * Hantera ECO-trösklar för harsh driving och green driving
 * Stöder både globala och organisationsspecifika inställningar
 */

import { useState, useEffect } from 'react';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import {
  ArrowLeftIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ArrowPathIcon,
  BuildingOffice2Icon,
  GlobeAltIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

interface EcoSettings {
  id: number;
  organizationId: string | null;
  maxAcceleration: number;
  maxBraking: number;
  maxCornering: number;
  excessiveIdlingSeconds: number;
  overspeedPercent: number;
  greenMaxAcceleration: number;
  greenMaxBraking: number;
  greenMinSpeed: number;
  greenMinDurationSeconds: number;
  organizationName?: string;
}

interface Organization {
  id: string;
  name: string;
  is_top_node: boolean;
}

const EcoSettings = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [allSettings, setAllSettings] = useState<EcoSettings[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('global');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    maxAcceleration: 4.03,
    maxBraking: 4.34,
    maxCornering: 5.58,
    excessiveIdlingSeconds: 300,
    overspeedPercent: 10,
    greenMaxAcceleration: 1.5,
    greenMaxBraking: 2.0,
    greenMinSpeed: 10,
    greenMinDurationSeconds: 120,
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadSettingsForOrg(selectedOrgId);
  }, [selectedOrgId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Ladda alla settings och toppnod-organisationer parallellt
      const [settingsRes, orgsRes] = await Promise.all([
        api.get('/api/eco-settings'),
        api.get('/api/organizations?topNodesOnly=true'),
      ]);
      
      if (settingsRes.data.success) {
        setAllSettings(settingsRes.data.data);
      }
      
      if (orgsRes.data.success || orgsRes.data.data) {
        // Filtrera endast toppnoder
        const topNodes = (orgsRes.data.data || orgsRes.data).filter(
          (org: Organization) => org.is_top_node
        );
        setOrganizations(topNodes);
      }
      
      // Ladda globala inställningar som standard
      await loadSettingsForOrg('global');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kunde inte ladda data');
    } finally {
      setLoading(false);
    }
  };

  const loadSettingsForOrg = async (orgId: string) => {
    try {
      const response = await api.get(`/api/eco-settings/${orgId}`);
      if (response.data.success) {
        const data = response.data.data;
        setFormData({
          maxAcceleration: data.maxAcceleration,
          maxBraking: data.maxBraking,
          maxCornering: data.maxCornering,
          excessiveIdlingSeconds: data.excessiveIdlingSeconds,
          overspeedPercent: data.overspeedPercent,
          greenMaxAcceleration: data.greenMaxAcceleration,
          greenMaxBraking: data.greenMaxBraking,
          greenMinSpeed: data.greenMinSpeed,
          greenMinDurationSeconds: data.greenMinDurationSeconds,
        });
      }
    } catch {
      // Error loading settings for org - handled silently
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      
      const response = await api.put(`/api/eco-settings/${selectedOrgId}`, formData);
      if (response.data.success) {
        setSuccess(true);
        
        // Uppdatera listan med alla settings
        const settingsRes = await api.get('/api/eco-settings');
        if (settingsRes.data.success) {
          setAllSettings(settingsRes.data.data);
        }
        
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('ecoSettings.couldNotSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (orgId: string) => {
    if (!confirm(t('ecoSettings.confirmDelete'))) {
      return;
    }
    
    try {
      await api.delete(`/api/eco-settings/${orgId}`);
      
      // Uppdatera listan
      const settingsRes = await api.get('/api/eco-settings');
      if (settingsRes.data.success) {
        setAllSettings(settingsRes.data.data);
      }
      
      // Om vi hade den valda, växla till global
      if (selectedOrgId === orgId) {
        setSelectedOrgId('global');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('ecoSettings.couldNotDelete'));
    }
  };

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0,
    }));
  };

  // Kolla om vald organisation har egna inställningar
  const hasOwnSettings = (orgId: string): boolean => {
    if (orgId === 'global') return true;
    return allSettings.some(s => s.organizationId === orgId);
  };

  // Hämta organisations namn
  const getOrgName = (orgId: string): string => {
    if (orgId === 'global') return t('ecoSettings.globalDefaults');
    const org = organizations.find(o => o.id === orgId);
    return org?.name || orgId;
  };

  if (loading) {
    return (
      <div className="p-6 bg-bgContainer rounded-lg h-full flex items-center justify-center">
        <ArrowPathIcon className="w-8 h-8 animate-spin text-bgPrimary" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-bgContainer rounded-lg h-full overflow-auto">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(ROUTES.SYSADMIN)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
          <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
            <Cog6ToothIcon className="w-6 h-6 text-primary" />
            {t('sysAdmin.ecoSettings.title') || 'ECO Driving Inställningar'}
          </h1>
          <p className="text-sm text-textSecondary">
            {t('sysAdmin.ecoSettings.description') || 'Konfigurera trösklar för ECO driving-händelser'}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
          <CheckCircleIcon className="w-5 h-5 text-green-500" />
          <span className="text-green-700 dark:text-green-300">
            {t('sysAdmin.ecoSettings.saved') || 'Inställningar sparade!'}
          </span>
        </div>
      )}

      {/* Organisationsväljare */}
      <div className="mb-6 bg-bgElevated rounded-lg border border-border p-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-textSecondary mb-2">
              {t('sysAdmin.ecoSettings.selectOrganization') || 'Välj organisation'}
            </label>
            <div className="flex gap-2">
              <select
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                className="flex-1 px-3 py-2 bg-bgContainer border border-border rounded-md text-textDefault focus:outline-none focus:ring-2 focus:ring-bgPrimary"
              >
                <option value="global">{t('ecoSettings.globalDefaultsOption')}</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    🏢 {org.name} {hasOwnSettings(org.id) ? '✓' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex items-end gap-2">
            {selectedOrgId !== 'global' && (
              <div className="flex items-center gap-2">
                {hasOwnSettings(selectedOrgId) ? (
                  <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                    <CheckCircleIcon className="w-4 h-4" />
                    {t('ecoSettings.hasOwnSettings')}
                  </span>
                ) : (
                  <span className="text-xs text-textTertiary flex items-center gap-1">
                    <GlobeAltIcon className="w-4 h-4" />
                    {t('ecoSettings.usesGlobal')}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Lista över organisationer med egna inställningar */}
        {allSettings.filter(s => s.organizationId !== null).length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-sm font-medium text-textSecondary mb-2">
              {t('ecoSettings.orgsWithOwnSettings')}
            </p>
            <div className="flex flex-wrap gap-2">
              {allSettings
                .filter(s => s.organizationId !== null)
                .map(s => {
                  const org = organizations.find(o => o.id === s.organizationId);
                  return (
                    <div
                      key={s.organizationId}
                      className="flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md text-sm"
                    >
                      <BuildingOffice2Icon className="w-4 h-4 text-blue-500" />
                      <span className="text-blue-700 dark:text-blue-300">
                        {org?.name || s.organizationId}
                      </span>
                      <button
                        onClick={() => handleDelete(s.organizationId!)}
                        className="ml-1 p-0.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                        title={t('ecoSettings.removeOrgSettings')}
                      >
                        <TrashIcon className="w-3 h-3 text-red-500" />
                      </button>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {/* Aktuell konfiguration */}
      <div className="mb-4 flex items-center gap-2">
        {selectedOrgId === 'global' ? (
          <GlobeAltIcon className="w-5 h-5 text-bgPrimary" />
        ) : (
          <BuildingOffice2Icon className="w-5 h-5 text-bgPrimary" />
        )}
        <h2 className="text-lg font-semibold text-textDefault">
          {getOrgName(selectedOrgId)}
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Harsh Driving Settings */}
        <div className="bg-bgElevated rounded-lg border border-border p-6">
          <h2 className="text-lg font-semibold text-textDefault mb-4 flex items-center gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
            {t('sysAdmin.ecoSettings.harshDriving') || 'Hård Körning'}
          </h2>
          
          <p className="text-sm text-textSecondary mb-4">
            {t('sysAdmin.ecoSettings.harshDrivingDescription') || 
              'Tröskelvärden för att detektera hård acceleration, bromsning och kurvtagning.'}
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-textSecondary mb-1">
                {t('sysAdmin.ecoSettings.maxAcceleration') || 'Max Acceleration'} (m/s²)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.maxAcceleration}
                onChange={(e) => handleChange('maxAcceleration', e.target.value)}
                className="w-full px-3 py-2 bg-bgContainer border border-border rounded-md text-textDefault focus:outline-none focus:ring-2 focus:ring-bgPrimary"
              />
              <p className="text-xs text-textSecondary mt-1">
                Teltonika standard: 4.03 m/s²
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-textSecondary mb-1">
                {t('sysAdmin.ecoSettings.maxBraking') || 'Max Inbromsning'} (m/s²)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.maxBraking}
                onChange={(e) => handleChange('maxBraking', e.target.value)}
                className="w-full px-3 py-2 bg-bgContainer border border-border rounded-md text-textDefault focus:outline-none focus:ring-2 focus:ring-bgPrimary"
              />
              <p className="text-xs text-textSecondary mt-1">
                Teltonika standard: 4.34 m/s²
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-textSecondary mb-1">
                {t('sysAdmin.ecoSettings.maxCornering') || 'Max Kurvtagning'} (m/s²)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.maxCornering}
                onChange={(e) => handleChange('maxCornering', e.target.value)}
                className="w-full px-3 py-2 bg-bgContainer border border-border rounded-md text-textDefault focus:outline-none focus:ring-2 focus:ring-bgPrimary"
              />
              <p className="text-xs text-textSecondary mt-1">
                Teltonika standard: 5.58 m/s²
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-textSecondary mb-1">
                {t('sysAdmin.ecoSettings.excessiveIdling') || 'Maximal Tomgångstid'} (sekunder)
              </label>
              <input
                type="number"
                value={formData.excessiveIdlingSeconds}
                onChange={(e) => handleChange('excessiveIdlingSeconds', e.target.value)}
                className="w-full px-3 py-2 bg-bgContainer border border-border rounded-md text-textDefault focus:outline-none focus:ring-2 focus:ring-bgPrimary"
              />
              <p className="text-xs text-textSecondary mt-1">
                Standard: 300 sekunder (5 minuter)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-textSecondary mb-1">
                {t('sysAdmin.ecoSettings.overspeedPercent') || 'Hastighetsöverträdelse'} (%)
              </label>
              <input
                type="number"
                value={formData.overspeedPercent}
                onChange={(e) => handleChange('overspeedPercent', e.target.value)}
                className="w-full px-3 py-2 bg-bgContainer border border-border rounded-md text-textDefault focus:outline-none focus:ring-2 focus:ring-bgPrimary"
              />
              <p className="text-xs text-textSecondary mt-1">
                {t('ecoSettings.defaultSpeedOverLimit')}
              </p>
            </div>
          </div>
        </div>

        {/* Green Driving Settings */}
        <div className="bg-bgElevated rounded-lg border border-border p-6">
          <h2 className="text-lg font-semibold text-textDefault mb-4 flex items-center gap-2">
            <CheckCircleIcon className="w-5 h-5 text-green-500" />
            {t('sysAdmin.ecoSettings.greenDriving') || 'Grön Körning'}
          </h2>
          
          <p className="text-sm text-textSecondary mb-4">
            {t('sysAdmin.ecoSettings.greenDrivingDescription') || 
              'Tröskelvärden för att belöna ekonomisk och miljövänlig körning.'}
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-textSecondary mb-1">
                {t('sysAdmin.ecoSettings.greenMaxAcceleration') || 'Max Acceleration (grön)'} (m/s²)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.greenMaxAcceleration}
                onChange={(e) => handleChange('greenMaxAcceleration', e.target.value)}
                className="w-full px-3 py-2 bg-bgContainer border border-border rounded-md text-textDefault focus:outline-none focus:ring-2 focus:ring-bgPrimary"
              />
              <p className="text-xs text-textSecondary mt-1">
                {t('ecoSettings.defaultSmoothAccel')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-textSecondary mb-1">
                {t('sysAdmin.ecoSettings.greenMaxBraking') || 'Max Inbromsning (grön)'} (m/s²)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.greenMaxBraking}
                onChange={(e) => handleChange('greenMaxBraking', e.target.value)}
                className="w-full px-3 py-2 bg-bgContainer border border-border rounded-md text-textDefault focus:outline-none focus:ring-2 focus:ring-bgPrimary"
              />
              <p className="text-xs text-textSecondary mt-1">
                {t('ecoSettings.defaultSmoothBrake')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-textSecondary mb-1">
                {t('sysAdmin.ecoSettings.greenMinSpeed') || 'Minimum Hastighet'} (km/h)
              </label>
              <input
                type="number"
                value={formData.greenMinSpeed}
                onChange={(e) => handleChange('greenMinSpeed', e.target.value)}
                className="w-full px-3 py-2 bg-bgContainer border border-border rounded-md text-textDefault focus:outline-none focus:ring-2 focus:ring-bgPrimary"
              />
              <p className="text-xs text-textSecondary mt-1">
                {t('ecoSettings.defaultMinSpeed')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-textSecondary mb-1">
                {t('sysAdmin.ecoSettings.greenMinDuration') || 'Minimum Varaktighet'} (sekunder)
              </label>
              <input
                type="number"
                value={formData.greenMinDurationSeconds}
                onChange={(e) => handleChange('greenMinDurationSeconds', e.target.value)}
                className="w-full px-3 py-2 bg-bgContainer border border-border rounded-md text-textDefault focus:outline-none focus:ring-2 focus:ring-bgPrimary"
              />
              <p className="text-xs text-textSecondary mt-1">
                {t('ecoSettings.defaultIdleTime')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Info box */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-start gap-3">
        <InformationCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p className="font-medium mb-1">
            {t('sysAdmin.ecoSettings.infoTitle') || 'Information'}
          </p>
          <p>
            {t('sysAdmin.ecoSettings.infoText') || 
              'Dessa värden används för att detektera ECO driving-händelser i realtid. Lägre värden för hård körning ger fler varningar, medan högre värden för grön körning kräver mer ekonomisk körning för att få gröna betyg.'}
          </p>
        </div>
      </div>

      {/* Save button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-bgPrimary text-white rounded-lg hover:bg-bgPrimaryHover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {saving && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
          {saving ? 
            (t('common.saving') || 'Sparar...') : 
            (t('common.save') || 'Spara')}
        </button>
      </div>
    </div>
  );
};

export default EcoSettings;

